import type {ReactNode} from 'react';
import _ from 'underscore';

import {extractTextFromReactNode} from 'llm/markdown/LLMMarkdownUtils';
import notifications from 'notifications';
import {checkClipboardWriteAccess, ClipboardItemType} from 'util/clipboard/ClipboardUtil';

/**
 * Builds an HTML string representing a table that is recognizable by Benchling's notebook
 * paste handling (`parseTable`). When the resulting HTML is pasted into a Benchling notebook
 * entry, it is converted into a first-class notebook table.
 */
export function buildLLMTableHTML(headers: Array<string>, rows: Array<Array<string>>): string {
  const headerHTML = headers
    .map((header) => `<th style="border:1px solid black;padding:0 10px">${_.escape(header)}</th>`)
    .join('');
  const bodyRowsHTML = rows
    .map(
      (row) =>
        `<tr>${row
          .map((cell) => `<td style="border:1px solid black;padding:0 10px">${_.escape(cell)}</td>`)
          .join('')}</tr>`
    )
    .join('');
  return (
    `<table style="border-spacing:0;border-collapse:collapse" class="benchling-header-row">` +
    `<thead><tr>${headerHTML}</tr></thead>` +
    `<tbody>${bodyRowsHTML}</tbody>` +
    `</table>`
  );
}

/**
 * Builds a tab-separated plain text representation of a table for a plain-text clipboard fallback.
 */
export function buildLLMTableText(headers: Array<string>, rows: Array<Array<string>>): string {
  const escapeCell = (cell: string): string => cell.replace(/\t/g, ' ').replace(/\r?\n/g, ' ');
  const headerLine = headers.map((cell) => escapeCell(cell)).join('\t');
  const bodyLines = rows.map((row) => row.map((cell) => escapeCell(cell)).join('\t'));
  return [headerLine, ...bodyLines].join('\n');
}

export interface LLMTableClipboardData {
  headers: Array<string>;
  /** Each cell is a list of React nodes (typically the non-citation children of the cell). */
  rows: Array<Array<Array<ReactNode>>>;
}

const COPY_FAILURE_MESSAGE = 'Unable to copy the table to your clipboard. Please try again.';

/**
 * Writes a Benchling-compatible HTML representation of the given table data to the clipboard,
 * along with a tab-separated plain-text fallback. When pasted into a Benchling notebook entry,
 * the resulting clipboard contents will be converted into a native notebook table.
 *
 * Citations and other rich elements should be stripped from the cell contents by the caller
 * prior to calling this function (by passing only non-citation children per cell).
 *
 * Shows a notification to the user on failure. Returns true if the write succeeded, false
 * otherwise.
 */
export async function copyLLMTableToClipboard({headers, rows}: LLMTableClipboardData): Promise<boolean> {
  const allowed = await checkClipboardWriteAccess();
  if (!allowed) {
    // checkClipboardWriteAccess already surfaces a notification explaining the browser/permission
    // issue, so we don't add another one here.
    return false;
  }
  const textRows = rows.map((row) => row.map((cell) => extractTextFromReactNode(cell).trim()));
  const html = buildLLMTableHTML(headers, textRows);
  const text = buildLLMTableText(headers, textRows);
  // If the rich clipboard API is unavailable (older browsers, some embedded webviews), fall back
  // to writing just the plain-text representation so the user still gets a usable copy.
  if (typeof window.ClipboardItem === 'undefined') {
    try {
      await navigator.clipboard.writeText(text);
      return true;
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch {
      notifications.error(COPY_FAILURE_MESSAGE);
      return false;
    }
  }
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        [ClipboardItemType.HTML]: new Blob([html], {type: ClipboardItemType.HTML}),
        [ClipboardItemType.TEXT]: new Blob([text], {type: ClipboardItemType.TEXT}),
      }),
    ]);
    return true;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch {
    notifications.error(COPY_FAILURE_MESSAGE);
    return false;
  }
}
